import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import { FC, useEffect, useState } from 'react'
import {
  BillType,
  ITables,
  KITCHEN_ITEM_STATUS,
  ORDER_TYPE,
} from '../../all-types'
import { CommonBtn } from '../../components'
import { useCurrentBill } from '../../hooks'
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks'
import {
  createLatestBill,
  handleModifyBill,
  updateCartItemOfTheTable,
} from '../../services'
import { BillCalculator } from '../billing'
import { BillConfimPopup } from '../billing/components'
import { BillProcessDrawer } from '../billing/modules'
import { setSelectedTable } from '../../redux-store'
import { useNavigate } from 'react-router-dom'

interface CartPanelItemDetailsProps {
  showBillingScreen: () => void
  goBack?: () => void
  isSummary: boolean
  freeTheTable: () => void
}

const ProductsCart: FC<CartPanelItemDetailsProps> = ({
  showBillingScreen,
  goBack,
  isSummary,
  freeTheTable,
}) => {
  const { fullBill, setFullBill } = useCurrentBill()
  const isCartEmpty = fullBill && fullBill.items.length <= 0
  const dispatcher = useAppDispatch();
  const navigate = useNavigate();
  const [openConfimPopup, setConfimPopup] = useState(false)
  const handleOpenConfimPopup = () => setConfimPopup(true)
  const handleCloseConfimPopup = () => setConfimPopup(false)
  const [loadBilling, setLoadBilling] = useState(true)
  const [createdBill, setCreatedBill] = useState<BillType | null>(null)
  const allItemsServed =
    fullBill?.items?.some(
      (i) => i.kitchInfo?.status !== KITCHEN_ITEM_STATUS.SERVED
    ) || false
  const selectedTable = useAppSelector((state) => state.table.selectedTable)
  const isTakeAwayTable = selectedTable?._id === '999'
  const isModifyBill = selectedTable?.tableName === 'Modify Bill'
  const discounts =
    useAppSelector(
      (state) => state.clientInfo.data.discountManager?.activeDiscounts
    ) || []
  const [selectedDiscount, setSelectedDiscount] = useState<string>('NONE')
  const [openShareBillDrawer, setOpenShareBillDrawer] = useState(false)
  const allProducts = useAppSelector((state) => state.allproducts.data)
  const clientInfo = useAppSelector((state) => state.clientInfo.data)

  const toggleShareBillDrawer = (newOpen: boolean) => {
    setOpenShareBillDrawer(newOpen)
  }

  useEffect(() => {
    applyDiscountOnFullBill()
  }, [selectedDiscount, fullBill?.modeOfPayment])

  const proceedForBilling = () => {
    try {
    } catch (e) {
      console.error(e)
    } finally {
      showBillingScreen()
    }
  }

  const applyDiscountOnFullBill = () => {
    try {
      if (selectedDiscount === 'NONE') {
        return
      }
      const fullBillClone = JSON.parse(JSON.stringify(fullBill))
      if (
        fullBillClone &&
        fullBillClone.items &&
        fullBillClone.items.length > 0
      ) {
        const billCalcObj = new BillCalculator(
          fullBillClone,
          allProducts,
          clientInfo
        )

        billCalcObj.applyFullBillDiscount([selectedDiscount])
        setFullBill(fullBillClone)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const startCreatingBillProcess = async () => {
    try {
      setLoadBilling(true)
      handleCloseConfimPopup()

      if (fullBill && selectedTable) {
        const clonedTable: ITables = JSON.parse(JSON.stringify(selectedTable))
        clonedTable.billSession = null

        await dispatcher(
          createLatestBill({
            bill: {
              ...fullBill,
              orderType: isTakeAwayTable
                ? ORDER_TYPE.TAKEAWAY
                : ORDER_TYPE['DINE-IN'],
              info: {
                createdBy: 'DEFAULT',
                tableId: selectedTable._id || '',
                tableName: selectedTable.tableName,
                tableNo: String(selectedTable.tableNo),
                sessionId: '',
              },
            },
          })
        )
        await dispatcher(updateCartItemOfTheTable({ table: clonedTable }))

        //setCreatedBill(resp.payload);
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoadBilling(false)
      toggleShareBillDrawer(true)
    }
  }

  const modifyBillProcess = async () => {
    try {
      setLoadBilling(true)
      handleCloseConfimPopup()
      if (fullBill && selectedTable) {
        await handleModifyBill(fullBill)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoadBilling(false);
      await closeTheModifyBill();
    }
  }

  const closeTheModifyBill = async () => {
    try {
      await dispatcher(setSelectedTable(null));
    } catch (e) {
      console.error(e)
    } finally {
      navigate("/app/counter?tab=billing-history")
    }
  }

  if (!fullBill) {
    return (
      <Stack
        flex={1}
        width={'100%'}
        justifyContent={'center'}
        alignItems={'center'}
        p={2}
      >
        <Typography variant="h6" color="error">
          No items in the cart!
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack flex={1} width={'100%'} justifyContent={'flex-end'} p={2}>
      {isSummary && (
        <Stack>
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 200,
            }}
          >
            <InputLabel id="floor-select-label">Choose Discount</InputLabel>
            <Select
              labelId="floor-select-label"
              value={selectedDiscount}
              onChange={(e: SelectChangeEvent) =>
                setSelectedDiscount(e.target.value)
              }
              label="Select Discount"
            >
              <MenuItem value={'NONE'}>NONE</MenuItem>
              {Object.values(discounts)?.map((v) => (
                <MenuItem key={v.discountId} value={v.discountId}>
                  Discount {v.discountName} ({v.applicableDiscount}{' '}
                  {v.discountType === 'PERCENTAGE' ? '%' : 'Rs'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack flexDirection={'row'} justifyContent={'space-between'}>
            <Typography variant="body1">Total Items</Typography>
            <Typography variant="body1">{fullBill.items?.length}</Typography>
          </Stack>
          <Stack flexDirection={'row'} justifyContent={'space-between'}>
            <Typography variant="body1">Subtotal</Typography>
            <Typography variant="body1">
              {fullBill.billingInfo.totalItemsCostRaw}
            </Typography>
          </Stack>

          <Stack flexDirection={'row'} justifyContent={'space-between'}>
            <Typography variant="body1">Discount</Typography>
            <Typography variant="body1">
              {fullBill.billingInfo.totalDiscountIssued}
            </Typography>
          </Stack>

          <Stack flexDirection={'row'} justifyContent={'space-between'}>
            <Typography variant="body1">Tax</Typography>
            <Typography variant="body1">
              {fullBill.billingInfo.totalTaxCharged.toFixed(2)}
            </Typography>
          </Stack>

          <Stack flexDirection={'row'} justifyContent={'space-between'}>
            <Typography variant="h6">Final Amount</Typography>
            <Typography variant="h6">
              {fullBill.billingInfo.finalBillAmount}
            </Typography>
          </Stack>
        </Stack>
      )}

      {isSummary ? (
        <Stack flexDirection={'row'} justifyContent={'space-between'} gap={1}>
          {isModifyBill ? (
            <>
              <CommonBtn
                variant="contained"
                color="success"
                disabled={fullBill.modeOfPayment === ''}
                fullWidth
                sx={{ height: 50 }}
                onClick={modifyBillProcess}
              >
                Update Bill
              </CommonBtn>
              <CommonBtn
                variant="contained"
                color="error"
                onClick={closeTheModifyBill}
                fullWidth
                sx={{ height: 50 }}
              >
                Close
              </CommonBtn>
            </>
          ) : (
            <>
              <CommonBtn
                variant="contained"
                color="error"
                onClick={goBack}
                fullWidth
                sx={{ height: 50 }}
              >
                Back
              </CommonBtn>
              <CommonBtn
                variant="contained"
                color="success"
                disabled={fullBill.modeOfPayment === ''}
                fullWidth
                sx={{ height: 50 }}
                onClick={handleOpenConfimPopup}
              >
                Place Order
              </CommonBtn>
            </>
          )}
        </Stack>
      ) : (
        <Stack flexDirection={'row'} justifyContent={'space-between'} gap={1}>
          {((!isCartEmpty && !allItemsServed) || isTakeAwayTable) && (
            <CommonBtn
              variant="contained"
              color="success"
              fullWidth
              sx={{ height: 50 }}
              onClick={proceedForBilling}
            >
              Proceed
            </CommonBtn>
          )}
        </Stack>
      )}

      <BillConfimPopup
        handleClose={handleCloseConfimPopup}
        onConfirm={startCreatingBillProcess}
        open={openConfimPopup}
      />
      <BillProcessDrawer
        isLoading={loadBilling}
        open={openShareBillDrawer}
        handleRemoveTab={() => console.log('handleRemovetab')}
        //toggleDrawer={toggleShareBillDrawer}
      />
    </Stack>
  )
}

export { ProductsCart }
